import jsPDF from "jspdf";
import "jspdf-autotable";
import { currencyFormat } from "../../../Utils/formatCurrency";
import { API_URL } from "../../../API/RestApi";

const resolveAssetUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = String(API_URL || "").replace(/\/$/, "");
  return `${base}${String(url).startsWith("/") ? url : `/${url}`}`;
};

const imageAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const res = await fetch(resolveAssetUrl(url));
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
};

const companyAddressText = (company = {}) =>
  [
    company.address,
    [company.city, company.province, company.postal_code].filter(Boolean).join(" "),
    company.country,
  ]
    .filter(Boolean)
    .join(", ");

const drawContainedImage = (doc, dataUrl, x, y, boxWidth, boxHeight) => {
  if (!dataUrl) return false;
  try {
    const props = doc.getImageProperties(dataUrl);
    const sourceWidth = Number(props?.width || 1);
    const sourceHeight = Number(props?.height || 1);
    const scale = Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight);
    const width = sourceWidth * scale;
    const height = sourceHeight * scale;
    const drawX = x + (boxWidth - width) / 2;
    const drawY = y + (boxHeight - height) / 2;
    doc.addImage(dataUrl, props?.fileType, drawX, drawY, width, height, undefined, "FAST");
    return true;
  } catch (_) {
    return false;
  }
};

export const exportSalaryPdf = async (data, company = {}) => {
  if (!data) return;

  const doc = new jsPDF();
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const month = data.month;
  const year = data.year;
  const companyName = company.company_name || "Nama Perusahaan";
  const slipTitle = company.slip_title || "SLIP GAJI";

  const benefits = (data.pay_details || []).filter(
    (item) =>
      (item.category === "benefit" || item.pay_title?.type === "benefit") &&
      item.pay_title?.name !== "Gaji dibayarkan" &&
      Number(item.allowance || 0) !== 0
  );
  const costs = (data.pay_details || []).filter(
    (item) =>
      (item.category === "cost" || item.pay_title?.type === "cost") &&
      item.pay_title?.name !== "Gaji dibayarkan" &&
      Number(item.allowance || 0) !== 0
  );

  const benefitRows = benefits.map((item) => [
    item.pay_title?.slip_name || item.pay_title?.name || item.code || "Komponen",
    currencyFormat(item.allowance),
  ]);
  const costRows = costs.map((item) => [
    item.pay_title?.slip_name || item.pay_title?.name || item.code || "Komponen",
    currencyFormat(item.allowance),
  ]);

  const benefitTotal = benefits.reduce((sum, item) => sum + Number(item.allowance || 0), 0);
  const costTotal = costs.reduce((sum, item) => sum + Number(item.allowance || 0), 0);
  benefitRows.push([
    { content: "Total Tunjangan", styles: { fontStyle: "bold" } },
    { content: currencyFormat(benefitTotal), styles: { fontStyle: "bold", halign: "right" } },
  ]);
  costRows.push([
    { content: "Total Potongan", styles: { fontStyle: "bold" } },
    { content: currencyFormat(costTotal), styles: { fontStyle: "bold", halign: "right" } },
  ]);

  // Header dibagi 3 area: logo, identitas perusahaan, dan judul slip.
  // Logo selalu di-contain agar rasio aslinya tidak gepeng/tertarik.
  const logoBox = { x: margin, y: 12, w: 28, h: 24 };
  const companyX = margin + 34;
  const rightBlockWidth = 47;
  const rightBlockX = pageWidth - margin - rightBlockWidth;
  const companyWidth = Math.max(55, rightBlockX - companyX - 6);
  const logo = await imageAsDataUrl(company.logo_url);
  drawContainedImage(doc, logo, logoBox.x, logoBox.y, logoBox.w, logoBox.h);

  doc.setTextColor(0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  const companyNameLines = doc.splitTextToSize(companyName, companyWidth);
  doc.text(companyNameLines, companyX, 18);

  let headerY = 18 + companyNameLines.length * 5.5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);

  const address = companyAddressText(company);
  if (address) {
    const addressLines = doc.splitTextToSize(address, companyWidth);
    doc.text(addressLines, companyX, headerY);
    headerY += addressLines.length * 4.3;
  }

  const contact = [
    company.phone && `Telp. ${company.phone}`,
    company.email,
    company.website,
  ]
    .filter(Boolean)
    .join(" • ");
  if (contact) {
    const contactLines = doc.splitTextToSize(contact, companyWidth);
    doc.text(contactLines, companyX, headerY);
    headerY += contactLines.length * 4.3;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(slipTitle, pageWidth - margin, 18, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const periodLines = doc.splitTextToSize(`Periode: ${month} ${year}`, rightBlockWidth);
  doc.text(periodLines, pageWidth - margin, 26, { align: "right" });

  // Separator mengikuti tinggi konten header terpanjang agar alamat tidak menabrak area berikutnya.
  const separatorY = Math.max(44, headerY + 3, logoBox.y + logoBox.h + 4);
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.line(margin, separatorY, pageWidth - margin, separatorY);

  const startInfoY = separatorY + 10;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("ID Karyawan", margin, startInfoY);
  doc.text("Nama", margin, startInfoY + 6);
  doc.text("Jabatan", margin, startInfoY + 12);
  doc.setFont("helvetica", "bold");
  doc.text(`: ${data.users?.users_code || "-"}`, margin + 30, startInfoY);
  doc.text(`: ${data.users?.name || "-"}`, margin + 30, startInfoY + 6);
  doc.text(`: ${data.users?.position?.name || "-"}`, margin + 30, startInfoY + 12);
  doc.setFont("helvetica", "normal");

  let y = startInfoY + 24;
  const commonTableOptions = {
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3, valign: "middle", halign: "left" },
    headStyles: {
      fillColor: [235, 238, 242],
      textColor: [20, 20, 20],
      fontStyle: "bold",
      halign: "left",
    },
    // Hanya kolom Jumlah yang rata kanan.
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "right", cellWidth: 52 },
    },
    margin: { left: margin, right: margin },
    tableWidth: pageWidth - margin * 2,
    didParseCell: (hookData) => {
      if (hookData.column.index === 1) hookData.cell.styles.halign = "right";
      else hookData.cell.styles.halign = "left";
    },
  };

  if (benefitRows.length > 1) {
    doc.setFont("helvetica", "bold");
    doc.text("Tunjangan / Penerimaan", margin, y);
    doc.autoTable({
      ...commonTableOptions,
      startY: y + 4,
      head: [["Komponen", "Jumlah"]],
      body: benefitRows,
    });
    y = doc.lastAutoTable.finalY + 9;
  }

  if (costRows.length > 1) {
    doc.setFont("helvetica", "bold");
    doc.text("Potongan / Pengurangan", margin, y);
    doc.autoTable({
      ...commonTableOptions,
      startY: y + 4,
      head: [["Komponen", "Jumlah"]],
      body: costRows,
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Gaji Bersih", margin, y + 5);
  doc.text(currencyFormat(data.net_salary), pageWidth - margin, y + 5, { align: "right" });

  if (company.footer_text) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);
    const footer = doc.splitTextToSize(company.footer_text, pageWidth - margin * 2);
    doc.text(footer, margin, Math.min(y + 18, 280));
  }

  doc.save(
    `Slip_Gaji_${String(data.users?.name || "Karyawan").replace(/[^a-z0-9_-]+/gi, "_")}_${month}_${year}.pdf`
  );
};
